import { FileIcon } from "lucide-react";
import type React from "react";
import { useTranslation } from "react-i18next";
import { useFileByGuid } from "@/hooks/useFileByGuid";
import { useInViewport } from "@/hooks/useInViewport";

interface FilePreviewProps {
  fileName: string;
  guid: string;
}

export const FilePreview: React.FC<FilePreviewProps> = ({ fileName, guid }) => {
  const { ref: containerRef, isInViewport } = useInViewport<HTMLDivElement>({
    rootMargin: "240px",
  });
  const { fileBlobUrl, isLoading, error } = useFileByGuid(guid, isInViewport);
  const { t } = useTranslation();

  if (!isInViewport || isLoading) {
    return (
      <div
        ref={containerRef}
        className="animate-pulse rounded-lg border border-gray-200 bg-gray-50 p-3"
      >
        <div className="flex items-center gap-2">
          <div className="size-4 rounded bg-gray-300" />
          <div className="h-4 w-32 rounded bg-gray-300" />
        </div>
      </div>
    );
  }

  if (error || !fileBlobUrl) {
    return (
      <div
        ref={containerRef}
        className="rounded-lg border border-gray-200 bg-gray-50 p-3"
      >
        <div className="flex items-center gap-2 text-red-600">
          <FileIcon className="size-4" />
          <span className="text-sm">
            {t("common.fileLoadFailed")}: {fileName}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef}>
      <a
        href={fileBlobUrl}
        target="_blank"
        rel="noopener noreferrer"
        download={fileName}
        className="block rounded-lg border border-gray-200 bg-white p-3 transition-shadow hover:shadow-md"
      >
        <div className="flex items-center gap-3">
          <FileIcon className="size-6 shrink-0 text-blue-500" />
          <div className="min-w-0 flex-1">
            <h4 className="truncate text-sm font-medium text-gray-900">
              {fileName}
            </h4>
            <p className="text-xs text-gray-400">
              {t("common.clickToDownload")}
            </p>
          </div>
        </div>
      </a>
    </div>
  );
};
